import { useEffect, useState } from 'react';
import { toast } from 'sonner';
import api from '@/lib/api';
import { usePartnerAuthStore, getApiErrorMessage, type Partner } from '@/stores/partnerAuthStore';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@/components/ui/table';
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
  AlertDialogTrigger,
} from '@/components/ui/alert-dialog';
import { PartnerFormDialog } from '@/components/partners/PartnerFormDialog';

function formatLastLogin(value: string | null): string {
  return value ? new Date(value).toLocaleString() : 'Never';
}

export function PartnersPage() {
  const { partner: currentPartner, setPartner } = usePartnerAuthStore();
  const [partners, setPartners] = useState<Partner[]>([]);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [editingPartner, setEditingPartner] = useState<Partner | null>(null);
  const [togglingUuid, setTogglingUuid] = useState<string | null>(null);
  /**
   * Bumped from an event handler to ask for a refetch. The fetch effect keys off
   * this, which keeps the "is a fetch outstanding" question answerable during
   * render instead of needing a flag an effect switches on.
   */
  const [reloadCount, setReloadCount] = useState(0);
  const [loadedCount, setLoadedCount] = useState<number | null>(null);
  const isLoading = loadedCount !== reloadCount;

  useEffect(() => {
    if (currentPartner?.role !== 'admin') return;

    // `active` is what makes a superseded response harmless: the cleanup runs
    // before the next fetch starts and on unmount, so a slow early response can
    // no longer repaint a table the user has already moved past.
    let active = true;

    void (async () => {
      try {
        const { data } = await api.get('/partners');
        if (!active) return;
        setPartners(data.data);
      } catch (err) {
        if (!active) return;
        toast.error(getApiErrorMessage(err));
      }
      if (active) setLoadedCount(reloadCount);
    })();

    return () => {
      active = false;
    };
  }, [currentPartner?.role, reloadCount]);

  if (currentPartner?.role !== 'admin') {
    return <p className="text-sm text-muted-foreground">You do not have access to this page.</p>;
  }

  function openCreateDialog() {
    setEditingPartner(null);
    setDialogOpen(true);
  }

  function openEditDialog(p: Partner) {
    setEditingPartner(p);
    setDialogOpen(true);
  }

  // If the admin edited or toggled their OWN row, the partner list refetch
  // updates the table but not usePartnerAuthStore, which drives the
  // navbar (name) and the admin-only nav link (role). Push the updated
  // record into the store too so those stay in sync without a reload.
  function syncCurrentPartner(updated: Partner) {
    if (currentPartner && updated.uuid === currentPartner.uuid) {
      setPartner(updated);
    }
  }

  function handlePartnerSaved(saved: Partner) {
    setReloadCount((count) => count + 1);
    syncCurrentPartner(saved);
  }

  async function toggleActive(p: Partner) {
    setTogglingUuid(p.uuid);
    try {
      const { data } = await api.patch(`/partners/${p.uuid}`, { is_active: !p.isActive });
      syncCurrentPartner(data.data);
      setReloadCount((count) => count + 1);
    } catch (err) {
      toast.error(getApiErrorMessage(err));
    } finally {
      setTogglingUuid(null);
    }
  }

  return (
    <div className="space-y-6">
      <div>
        <h1 className="font-display text-3xl font-bold">Partners</h1>
        <p className="text-muted-foreground">Manage back-office access and roles.</p>
      </div>
      <Card>
        <CardHeader className="flex flex-row items-center justify-between space-y-0">
          <CardTitle>Partners</CardTitle>
          <Button variant="accent" onClick={openCreateDialog}>Add partner</Button>
        </CardHeader>
        <CardContent>
          {isLoading ? (
            <p className="py-8 text-center text-sm text-muted-foreground">Loading...</p>
          ) : partners.length === 0 ? (
            <p className="py-8 text-center text-sm text-muted-foreground">No partners found.</p>
          ) : (
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead>Name</TableHead>
                  <TableHead>Email</TableHead>
                  <TableHead>Role</TableHead>
                  <TableHead>Status</TableHead>
                  <TableHead>Last login</TableHead>
                  <TableHead>Actions</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {partners.map((p) => (
                  <TableRow key={p.uuid}>
                    <TableCell className="whitespace-nowrap">{p.name}</TableCell>
                    <TableCell className="whitespace-nowrap">{p.email}</TableCell>
                    <TableCell>
                      <Badge variant="secondary" className="capitalize">{p.role}</Badge>
                    </TableCell>
                    <TableCell>
                      <Badge variant={p.isActive ? 'default' : 'outline'}>{p.isActive ? 'Active' : 'Inactive'}</Badge>
                    </TableCell>
                    <TableCell className="whitespace-nowrap">{formatLastLogin(p.lastLoginAt)}</TableCell>
                    <TableCell>
                      <div className="flex gap-2">
                        <Button variant="outline" size="sm" onClick={() => openEditDialog(p)}>
                          Edit
                        </Button>
                        {p.isActive ? (
                          <AlertDialog>
                            <AlertDialogTrigger asChild>
                              <Button variant="outline" size="sm" disabled={togglingUuid === p.uuid}>
                                Deactivate
                              </Button>
                            </AlertDialogTrigger>
                            <AlertDialogContent>
                              <AlertDialogHeader>
                                <AlertDialogTitle>Deactivate this partner?</AlertDialogTitle>
                                <AlertDialogDescription>
                                  They will be signed out and cannot sign back in until reactivated.
                                </AlertDialogDescription>
                              </AlertDialogHeader>
                              <AlertDialogFooter>
                                <AlertDialogCancel>Cancel</AlertDialogCancel>
                                <AlertDialogAction onClick={() => void toggleActive(p)}>Deactivate</AlertDialogAction>
                              </AlertDialogFooter>
                            </AlertDialogContent>
                          </AlertDialog>
                        ) : (
                          <Button
                            variant="outline"
                            size="sm"
                            disabled={togglingUuid === p.uuid}
                            onClick={() => void toggleActive(p)}
                          >
                            Activate
                          </Button>
                        )}
                      </div>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          )}
        </CardContent>
      </Card>
      <PartnerFormDialog
        open={dialogOpen}
        onOpenChange={setDialogOpen}
        partner={editingPartner}
        onSaved={handlePartnerSaved}
      />
    </div>
  );
}
